import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, LogOut, ChevronLeft, ChevronRight, ChevronDown, Clock3, Sparkles, Circle
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { groupNavItems } from '../lib/navigation';
import { preloadGamificationModule } from './GamificationModuleDeferred';
import { useVertical } from '../contexts/VerticalContext';
import { useBranding } from '../contexts/BrandingContext';

const RECENTS_KEY = 'hrms.recentNav.v1';
const RECENTS_MAX = 4;

const readRecents = () => {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((r) => r && r.id && r.label) : [];
  } catch {
    return [];
  }
};

const Sidebar = ({
  items,
  activeItem,
  onNavigate,
  isOpen,
  toggleSidebar,
  onLogout
}) => {
  const [expandedItems, setExpandedItems] = useState([]);
  const [recents, setRecents] = useState(readRecents);

  const shouldPreloadGamification = (item) =>
    item?.id === 'engagement-rewards' ||
    item?.id?.startsWith?.('gamification-') ||
    item?.subItems?.some?.((subItem) => String(subItem?.id || '').startsWith('gamification-'));

  const warmNavigationTarget = (item) => {
    if (shouldPreloadGamification(item)) {
      preloadGamificationModule().catch(() => {});
    }
  };

  const pushRecent = useCallback((id, label) => {
    if (!id || !label) return;
    setRecents((prev) => {
      const next = [{ id, label }, ...prev.filter((r) => r.id !== id)].slice(0, RECENTS_MAX);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — recents are best-effort */
      }
      return next;
    });
  }, []);

  // Auto-expand parent if child is active
  useEffect(() => {
    if (!items) return;

    const parent = items.find(item =>
      item.subItems?.some(sub => sub.id === activeItem)
    );
    if (parent && !expandedItems.includes(parent.id)) {
      setExpandedItems(prev => [...prev, parent.id]);
    }
    // The effect only responds to navigation changes, not manual expansion state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, items]);

  const navigateLeaf = (id, label) => {
    pushRecent(id, label);
    onNavigate(id);
    if (window.innerWidth < 768) toggleSidebar();
  };

  const handleItemClick = (item) => {
    warmNavigationTarget(item);
    if (item.subItems) {
      const itemIsExpanded = expandedItems.includes(item.id);
      const itemHasActiveChild = item.subItems.some((subItem) => subItem.id === activeItem);

      if (item.defaultSubItemId) {
        if (!isOpen) {
          toggleSidebar();
          setTimeout(() => {
            setExpandedItems((prev) =>
              prev.includes(item.id) ? prev : [...prev, item.id]
            );
          }, 300);
          if (!itemHasActiveChild && activeItem !== item.id) {
            onNavigate(item.defaultSubItemId);
          }
          return;
        }

        if (itemIsExpanded) {
          setExpandedItems((prev) => prev.filter((id) => id !== item.id));
          return;
        }

        setExpandedItems((prev) =>
          prev.includes(item.id) ? prev : [...prev, item.id]
        );
        if (!itemHasActiveChild && activeItem !== item.id) {
          onNavigate(item.defaultSubItemId);
        }
        return;
      }
      if (!isOpen) {
        toggleSidebar();
        // Give a small delay to let sidebar expand before showing sub-items
        setTimeout(() => {
          setExpandedItems(prev =>
            prev.includes(item.id) ? prev : [...prev, item.id]
          );
        }, 300);
      } else {
        setExpandedItems(prev =>
          prev.includes(item.id)
            ? prev.filter(id => id !== item.id)
            : [...prev, item.id]
        );
      }
    } else {
      navigateLeaf(item.id, item.label);
    }
  };

  const isExpanded = (id) => expandedItems.includes(id);
  const isCurrent = (item) => activeItem === item.id;
  const isAncestor = (item) => item.subItems?.some(sub => sub.id === activeItem);

  // Vertical Engine: overlay industry terminology + per-vertical nav labels onto the
  // existing nav. Only labels change — routing ids, icons, gating and sub-items are intact.
  const { term, bundle } = useVertical();
  const { appName, logoDarkUrl, branding } = useBranding();
  const sidebarColor = branding?.sidebar_color || null;
  const brandInitial = (appName || 'V').trim().charAt(0).toUpperCase() || 'V';
  const navLabelById = useMemo(() => {
    const map = {};
    (bundle?.navigation || []).forEach((n) => { if (n?.id && n?.label) map[n.id] = n.label; });
    return map;
  }, [bundle]);
  const relabel = useCallback((label, id) => {
    if (id && navLabelById[id]) return navLabelById[id];
    if (!label || typeof label !== 'string') return label;
    const emp = term('employees', 'Employees');
    const empSing = term('employee', 'Employee');
    const dept = term('department', 'Department');
    const branch = term('branch', 'Branch');
    return label
      .replace(/\bEmployees\b/g, emp)
      .replace(/\bEmployee\b/g, empSing)
      .replace(/\bDepartments\b/g, `${dept}s`)
      .replace(/\bDepartment\b/g, dept)
      .replace(/\bBranches\b/g, branch === 'Branch' ? 'Branches' : `${branch}s`)
      .replace(/\bBranch\b/g, branch);
  }, [navLabelById, term]);

  const groups = useMemo(() => groupNavItems(items).map((group) => ({
    ...group,
    items: (group.items || []).map((it) => ({
      ...it,
      label: relabel(it.label, it.id),
      subItems: it.subItems
        ? it.subItems.map((s) => ({ ...s, label: relabel(s.label, s.id) }))
        : it.subItems,
    })),
  })), [items, relabel]);
  const visibleRecents = recents.filter((r) => r.id !== activeItem).slice(0, RECENTS_MAX);

  const renderItem = (item) => {
    const current = isCurrent(item);
    const ancestor = isAncestor(item);
    return (
      <div key={item.id} className="relative">
        {/* Active rail indicator */}
        {(current || ancestor) && (
          <span
            aria-hidden
            className={cn(
              "absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full transition-all",
              current ? "bg-primary" : "bg-primary/40"
            )}
          />
        )}
        <button
          onClick={() => handleItemClick(item)}
          onMouseEnter={() => warmNavigationTarget(item)}
          onFocus={() => warmNavigationTarget(item)}
          aria-current={current ? 'page' : undefined}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-premium relative group",
            current
              ? "bg-primary text-primary-foreground font-semibold shadow-[0_4px_14px_-4px_hsl(214_94%_40%/0.55)]"
              : ancestor
                ? "bg-white/[0.07] text-white font-semibold"
                : "text-slate-300 hover:bg-white/[0.06] hover:text-white",
            !isOpen && "md:justify-center md:px-2"
          )}
          title={!isOpen ? item.label : undefined}
        >
          {/* A nav item whose iconKey is missing from the role's icon map used
              to render `undefined` as a component, which takes the entire app
              down rather than dropping one glyph. */}
          {item.icon ? (
            <item.icon
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors",
                current ? "text-primary-foreground" : "text-slate-400 group-hover:text-white"
              )}
            />
          ) : (
            <Circle
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors",
                current ? "text-primary-foreground" : "text-slate-400 group-hover:text-white"
              )}
            />
          )}
          <span className={cn("truncate flex-1 text-left", !isOpen && "md:hidden")}>
            {item.label}
          </span>

          {item.subItems && isOpen && (
            <ChevronDown className={cn(
              "h-3.5 w-3.5 transition-transform duration-200 opacity-60",
              isExpanded(item.id) && "rotate-180"
            )} />
          )}

          {/* Tooltip for collapsed state */}
          {!isOpen && (
            <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-900 text-slate-100 text-xs font-medium rounded-lg shadow-lg border border-white/10 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 hidden md:block animate-in fade-in slide-in-from-left-1 duration-200">
              {item.label}
            </div>
          )}
        </button>

        {/* Sub-items */}
        {item.subItems && isOpen && isExpanded(item.id) && (
          <div className="relative pl-9 space-y-0.5 mt-1 animate-in slide-in-from-top-1 duration-200">
            <div className="absolute left-[22px] top-1 bottom-1 w-px bg-white/10" />
            {item.subItems.map((subItem) => {
              const subActive = activeItem === subItem.id;
              return (
                <button
                  key={subItem.id}
                  onClick={() => {
                    warmNavigationTarget(subItem);
                    navigateLeaf(subItem.id, subItem.label);
                  }}
                  onMouseEnter={() => warmNavigationTarget(subItem)}
                  onFocus={() => warmNavigationTarget(subItem)}
                  aria-current={subActive ? 'page' : undefined}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] transition-colors relative",
                    subActive
                      ? "text-white font-medium bg-primary/15"
                      : "text-slate-400 hover:text-white hover:bg-white/[0.05]"
                  )}
                >
                  <span className={cn(
                    "h-1.5 w-1.5 rounded-full shrink-0 transition-colors",
                    subActive ? "bg-primary" : "bg-slate-600 group-hover:bg-slate-400"
                  )} />
                  <span className="truncate">{subItem.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar Container */}
      <div className={cn(
        "fixed md:static inset-y-0 left-0 z-50 border-r border-white/5 shadow-xl transform transition-all duration-300 ease-premium flex h-full min-h-0 flex-col",
        // slate-950 unless the workspace picked its own rail colour; the inline
        // style below wins when it did, so the class stays as the fallback.
        "bg-slate-950",
        isOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0 md:w-[76px]"
      )}
      style={sidebarColor ? { backgroundColor: sidebarColor } : undefined}>
        {/* Header / Brand */}
        <div className="h-16 flex items-center px-3 border-b border-white/5 justify-between gap-2 shrink-0">
          {/* The brand block spans the rail. `w-full` with `object-contain` lets a
              wide wordmark use the whole 256px and a square mark stay square —
              the height cap is what stops a tall logo from bursting the 64px
              header, so neither shape needs its own rule.

              The workspace's own mark when it has one, the product's otherwise.
              `logoDarkUrl` falls back to the light logo in the context, because
              one logo is the common case and this rail is always dark. */}
          <div className={cn("flex min-w-0 flex-1 items-center overflow-hidden", !isOpen && "md:hidden")}>
            <img
              src={logoDarkUrl || '/web-hr-logo-white.png'}
              alt={appName || 'WebHR'}
              className="w-full max-h-11 object-contain"
            />
          </div>

          {/* Collapsed brand mark */}
          <div className={cn("hidden", !isOpen && "md:flex md:w-full md:justify-center")}>
            {logoDarkUrl ? (
              <img src={logoDarkUrl} alt={appName || 'Workspace'} className="h-9 w-9 object-contain" />
            ) : (
              <div className="grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white shrink-0 shadow-[0_4px_12px_-2px_hsl(214_94%_40%/0.6)]">
                <span className="text-base font-bold tracking-tight">{brandInitial}</span>
              </div>
            )}
          </div>

          <Button variant="ghost" size="icon" className="md:hidden text-slate-300 hover:bg-white/10 hover:text-white" onClick={toggleSidebar}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4 px-2.5 hide-scrollbar">
          {/* Recently used */}
          {isOpen && visibleRecents.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <Clock3 className="h-3 w-3" /> Recent
              </div>
              <div className="space-y-0.5">
                {visibleRecents.map((r) => (
                  <button
                    key={`recent-${r.id}`}
                    onClick={() => navigateLeaf(r.id, r.label)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] text-slate-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                    <span className="truncate">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <nav className="space-y-1">
            {groups.map((group, gi) => (
              <div key={group.section || `group-${gi}`} className={cn(gi > 0 && "pt-2")}>
                {group.section && (
                  isOpen ? (
                    <div className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {group.section}
                    </div>
                  ) : (
                    <div className="mx-auto my-2 h-px w-6 bg-white/10 md:block hidden" />
                  )
                )}
                <div className="space-y-1">
                  {group.items.map(renderItem)}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Footer */}
        <div className="shrink-0 p-3 border-t border-white/5 space-y-1">
          <button
            onClick={onLogout}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors",
              !isOpen && "md:justify-center md:px-2"
            )}
            title={!isOpen ? "Logout" : undefined}
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            <span className={cn("truncate", !isOpen && "md:hidden")}>
              Logout
            </span>
          </button>

          {/* Desktop Collapse Toggle */}
          <div className="hidden md:block pt-1 border-t border-white/5">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "w-full text-slate-400 hover:bg-white/[0.06] hover:text-white gap-2",
                isOpen ? "justify-start px-3" : "justify-center px-2"
              )}
              onClick={toggleSidebar}
            >
              {isOpen ? (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  <span className="text-[13px]">Collapse</span>
                </>
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
